import { Fragment, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import { RiCloseLine } from '@remixicon/react'
import { cx } from '../../lib/utils'
import { Button } from './Button'

interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '3xl'
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '3xl': 'max-w-3xl',
}

export default function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
}: DrawerProps) {
  const { t } = useTranslation()

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 dark:bg-black/50 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel
                  className={cx(
                    'pointer-events-auto w-screen',
                    sizeClasses[size],
                  )}
                >
                  <div className="flex h-full flex-col bg-white dark:bg-gray-950 shadow-xl ring-1 ring-gray-200 dark:ring-gray-800">
                    {/* Header */}
                    <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-4 sm:px-6">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 break-words">
                          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-50">
                            {title}
                          </Dialog.Title>
                          {subtitle && (
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {subtitle}
                            </p>
                          )}
                        </div>
                        <div className="ml-3 flex h-7 items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="-m-1 size-10 p-0"
                            aria-label={t('common.close')}
                          >
                            <RiCloseLine className="size-5" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                      {children}
                    </div>

                    {/* Footer */}
                    {footer && (
                      <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-4 sm:px-6">
                        {footer}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export { Drawer }
